"use client";

import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  CARD,
  HINT,
  INPUT,
  LABEL,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { AbstractScreeningToolStrings } from "@/components/tools/abstract-screening-tool";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import type { RefRecord } from "@/lib/tool-kit/research-discovery/records";
import {
  type Decision,
  highlight,
  type Verdict,
} from "@/lib/tool-kit/research-discovery/screening";

const MARK_INCLUDE = "rounded bg-emerald-500/25 px-0.5 text-foreground";
const MARK_EXCLUDE = "rounded bg-rose-500/25 px-0.5 text-foreground";

function Highlighted({
  text,
  include,
  exclude,
}: {
  text: string;
  include: readonly string[];
  exclude: readonly string[];
}) {
  const segments = highlight(text, include, exclude);
  let offset = 0;
  return (
    <>
      {segments.map((segment) => {
        const key = offset;
        offset += segment.text.length;
        if (segment.kind === null) {
          return <span key={key}>{segment.text}</span>;
        }
        return (
          <mark
            className={segment.kind === "include" ? MARK_INCLUDE : MARK_EXCLUDE}
            key={key}
          >
            {segment.text}
          </mark>
        );
      })}
    </>
  );
}

function byline(record: RefRecord, etAl: string): string {
  const names = record.authors
    .slice(0, 4)
    .map((author) => author.literal ?? author.family);
  const people =
    record.authors.length > 4
      ? `${names.join(", ")} ${etAl}`
      : names.join(", ");
  return [people, record.year, record.container]
    .filter((part) => part !== "")
    .join(" · ");
}

const DECISION_ORDER: readonly Decision[] = ["include", "maybe", "exclude"];

export function ScreeningCard({
  record,
  position,
  total,
  verdict,
  duplicateOf,
  includeTerms,
  excludeTerms,
  strings,
  onDecide,
  onClear,
  onReason,
  onNote,
  onMove,
}: {
  record: RefRecord;
  position: number;
  total: number;
  verdict: Verdict | undefined;
  duplicateOf: number | undefined;
  includeTerms: readonly string[];
  excludeTerms: readonly string[];
  strings: AbstractScreeningToolStrings;
  onDecide: (decision: Decision) => void;
  onClear: () => void;
  onReason: (reason: string) => void;
  onNote: (note: string) => void;
  onMove: (step: -1 | 1) => void;
}) {
  const fmt = useToolNumber();
  const reasons: string[] = [...strings.reasons];
  const reason = verdict?.reason ?? "";
  if (reason !== "" && !reasons.includes(reason)) {
    reasons.push(reason);
  }
  const keys = { include: "I", maybe: "M", exclude: "E" } as const;

  return (
    <article aria-label={strings.cardLabel} className={`${CARD} space-y-4`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={HINT}>
          {t(strings.position, { position: fmt(position), total: fmt(total) })}
        </p>
        <div className="flex gap-2">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => onMove(-1)}
            type="button"
          >
            {strings.previous}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => onMove(1)}
            type="button"
          >
            {strings.next}
          </button>
        </div>
      </div>
      {duplicateOf === undefined ? null : (
        <p className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-foreground text-sm">
          {t(strings.duplicate, { record: fmt(duplicateOf + 1) })}
        </p>
      )}
      <div className="space-y-1">
        <h3 className={WIDGET_H3}>
          <Highlighted
            exclude={excludeTerms}
            include={includeTerms}
            text={record.title || strings.untitled}
          />
        </h3>
        <p className="text-muted-foreground text-sm">
          {byline(record, strings.etAl)}
        </p>
        {record.doi ? (
          <p className="break-all font-mono text-muted-foreground text-xs">
            <a
              className="text-primary underline-offset-4 hover:underline"
              href={`https://doi.org/${record.doi}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              {record.doi}
            </a>
          </p>
        ) : null}
      </div>
      <p className="whitespace-pre-line text-foreground text-sm leading-relaxed">
        {record.abstract === "" ? (
          <span className="text-muted-foreground">{strings.noAbstract}</span>
        ) : (
          <Highlighted
            exclude={excludeTerms}
            include={includeTerms}
            text={record.abstract}
          />
        )}
      </p>
      {record.keywords.length > 0 ? (
        <p className={HINT}>
          {strings.keywords} {record.keywords.join("; ")}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-3">
        {DECISION_ORDER.map((decision) => (
          <button
            aria-keyshortcuts={keys[decision]}
            aria-pressed={verdict?.decision === decision}
            className={
              verdict?.decision === decision ? BUTTON_PRIMARY : BUTTON_SECONDARY
            }
            key={decision}
            onClick={() => onDecide(decision)}
            type="button"
          >
            {strings.decisions[decision]} ({keys[decision]})
          </button>
        ))}
        <button
          aria-keyshortcuts="U"
          className={BUTTON_SECONDARY}
          disabled={verdict === undefined}
          onClick={onClear}
          type="button"
        >
          {strings.undo} (U)
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className={LABEL} htmlFor="ast-reason">
            {strings.reasonLabel}
          </label>
          <select
            className={INPUT}
            disabled={verdict?.decision !== "exclude"}
            id="ast-reason"
            onChange={(event) => onReason(event.target.value)}
            value={reason}
          >
            <option value="">{strings.reasonNone}</option>
            {reasons.map((option, index) => (
              <option key={option} value={option}>
                {index < strings.reasons.length
                  ? `${index + 1}. ${option}`
                  : option}
              </option>
            ))}
          </select>
          <p className={HINT}>{strings.reasonHint}</p>
        </div>
        <div className="space-y-2">
          <label className={LABEL} htmlFor="ast-note">
            {strings.noteLabel}
          </label>
          <input
            className={INPUT}
            disabled={verdict === undefined}
            id="ast-note"
            onChange={(event) => onNote(event.target.value)}
            placeholder={strings.notePlaceholder}
            value={verdict?.note ?? ""}
          />
        </div>
      </div>
    </article>
  );
}
